import { Layer } from "@carbon/react";
import { useProgressStepModal } from "@flaminem/ui-components";
import { selectUniqueItemsFromArray } from "@flaminem/utils";
import { FC, useMemo } from "react";

import { PassiveModal, ProgressStepModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors/mapToModalError.ts";
import {
  useConfirmFraudAlertMutation,
  useValidateComplainAlertMutation,
} from "src/graphql/anti-fraud/queries/alerts/mutations/ValidateAlertMutation.ts";
import { useFormatPropertyByCode } from "src/hooks/property/useFormatProperyByCode.ts";
import { useT } from "src/internationalization";
import { mapToApiAlertValidationDecision } from "src/mappers/alert.mappers.ts";
import { Alert, AlertValidationDecision } from "src/types/alert.ts";
import { cn } from "src/utils/string.utils.ts";

import { BlacklistIdentifiersForm, useBlacklistIdentifiersForm } from "./BlacklistIdentifiersForm";
import { DecisionForm } from "./DecisionForm/DecisionForm.tsx";
import { useBlockedByDecision } from "./DecisionForm/useBlockedByDecision.ts";
import { useDecisionForm } from "./DecisionForm/useDecisionForm.ts";
import { useFraudDetailsForm, FraudDetailsFormValidatedData } from "./FraudDetailsForm";
import { FraudDetailsForm } from "./FraudDetailsForm/FraudDetailsForm.tsx";

export type ValidateAlertModalProps = {
  onClose: () => void;
  alert: Alert;
  caseId: string;
};

export const ValidateAlertModal: FC<ValidateAlertModalProps> = ({ onClose, alert, caseId }) => {
  const t = useT();
  const decisionFormControl = useDecisionForm();
  const formatPropertyByCode = useFormatPropertyByCode();
  const { blockedByDecision, blockedReasonByDecision, hasBlockedDecisions } =
    useBlockedByDecision(alert);
  const allDecisionsBlocked = useMemo(
    () => blockedByDecision.size > 0 && Array.from(blockedByDecision.values()).every(Boolean),
    [blockedByDecision]
  );

  const decisionValue = decisionFormControl.methods.watch(
    "decision"
  ) as AlertValidationDecision | null;

  const fraudDetailsFormControl = useFraudDetailsForm({ decision: decisionValue });
  const blacklistIdentifiersFormControl = useBlacklistIdentifiersForm({ caseId });

  const { showSuccessToast } = useToast();

  const onSuccess = () => {
    showSuccessToast(t("alerts.confirmFraud.success.toast.message"));
    onClose();
  };

  const {
    performMutation: validateAlert,
    loading,
    error,
  } = useConfirmFraudAlertMutation({ onSuccess });

  const {
    performMutation: validateComplainAlert,
    loading: loadingComplainAlert,
    error: complainAlertError,
  } = useValidateComplainAlertMutation({ onSuccess });

  const submitModal = () => {
    const fraudDetailsFormData =
      fraudDetailsFormControl.getValues() as FraudDetailsFormValidatedData;

    const decision = decisionFormControl.getValues("decision") as AlertValidationDecision;
    const selectedIdentifiers = blacklistIdentifiersFormControl.getValues("selectedIdentifiers");

    if (!decision) {
      console.error("Decision is expected to be defined, but is not");
      return;
    }

    const commonVariables = {
      id: alert.id,
      decision: mapToApiAlertValidationDecision(decision),
      comment: fraudDetailsFormData.comment,
    };

    if (decision === "compliant") {
      validateComplainAlert(commonVariables);
    } else {
      validateAlert({
        ...commonVariables,
        amount:
          decision === "provenFraudWithNoFinancialLoss"
            ? null
            : fraudDetailsFormData.amount !== undefined
            ? Number(fraudDetailsFormData.amount)
            : null,
        fraudTypeCode: fraudDetailsFormData.fraudType.code,
        caseId: caseId,
        allowLinkedCaseIdentifiers: true,
        identifiersToBlacklist: selectUniqueItemsFromArray(
          selectedIdentifiers.map((identifier) => ({
            identifierCode: identifier.identifierCode,
            elementCode: identifier.elementCode,
            blacklistCode: identifier.blacklistCode,
            additionalProperties: identifier.additionalProperties ?? [],
          })),
          (a, b) => a.identifierCode === b.identifierCode
        ),
      });
    }
  };

  const submitDecisionForm = () => {
    decisionFormControl.submit(() => {
      stepsControl.goNextStep();
    })();
  };

  const submitFraudDetailsForm = () => {
    fraudDetailsFormControl.submit(() => {
      if (stepsControl.isLastStep()) {
        submitModal();
      } else {
        stepsControl.goNextStep();
      }
    })();
  };

  const submitBlacklistIdentifiersForm = () => {
    blacklistIdentifiersFormControl.submit(() => {
      submitModal();
    })();
  };

  const stepsConfig = [
    {
      type: "decision",
      title: t("alerts.confirmFraud.decision.tab.title"),
      subtitle: t("alerts.confirmFraud.decision.tab.subtitle"),
      invalid: decisionFormControl.hasError,
      submit: submitDecisionForm,
      focus: "[data-decision-input]:first-of-type",
    },
    {
      type: "details",
      title: t("alerts.confirmFraud.details.tab.title"),
      subtitle: t("alerts.confirmFraud.details.tab.subtitle"),
      invalid: fraudDetailsFormControl.hasError,
      submit: submitFraudDetailsForm,
      focus: "#amount",
    },
  ];

  if (decisionValue && decisionValue !== "compliant") {
    stepsConfig.push({
      type: "identifiers",
      title: t("alerts.confirmFraud.identifiers.tab.title"),
      subtitle: t("alerts.confirmFraud.identifiers.tab.subtitle"),
      invalid:
        blacklistIdentifiersFormControl.hasError ||
        !!blacklistIdentifiersFormControl.blacklistableIdentifiers.error,
      submit: submitBlacklistIdentifiersForm,
      focus: "#searchBlacklistableIdentifiers",
    });
  }

  const { control: stepsControl, modalProps } = useProgressStepModal(stepsConfig);

  const handleDecisionChange = () => {
    fraudDetailsFormControl.reset();
    blacklistIdentifiersFormControl.reset();
    stepsControl.reset();
  };

  const modalContent = (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto min-h-[540px]">
      <Layer
        level={0}
        className={cn("flex flex-col px-5", {
          hidden: !stepsControl.isStep("decision"),
        })}
      >
        <DecisionForm
          decisionFormControl={decisionFormControl}
          caseId={caseId}
          caseNumber={alert.alertNumber}
          onDecisionChange={handleDecisionChange}
          blockedByDecision={blockedByDecision}
          blockedReasonByDecision={blockedReasonByDecision}
          hasBlockedDecisions={hasBlockedDecisions}
        />
      </Layer>
      <Layer
        level={1}
        className={cn("flex flex-col px-5 h-full", {
          hidden: !stepsControl.isStep("details"),
        })}
      >
        <FraudDetailsForm
          fraudDetailsFormControl={fraudDetailsFormControl}
          decisionValue={decisionValue!}
          caseAmount={formatPropertyByCode(alert.currentCase.properties, "montant_demande")}
          onError={() => stepsControl.setStepError("details", true)}
        />
      </Layer>
      <div
        // Needed to reset table selection when decision changes
        key={decisionValue}
        className={cn("flex flex-col h-full overflow-hidden", {
          hidden: !stepsControl.isStep("identifiers"),
        })}
      >
        <BlacklistIdentifiersForm
          caseId={caseId}
          blacklistIdentifiersFormControl={blacklistIdentifiersFormControl}
        />
      </div>
    </div>
  );

  if (allDecisionsBlocked) {
    return (
      <PassiveModal
        onClose={onClose}
        title={t("alerts.confirmFraud.decision.blocked.title")}
        subtitle={t("alerts.confirmFraud.modal.subtitle", { number: alert.alertNumber })}
        size="lg"
      >
        {modalContent}
      </PassiveModal>
    );
  }

  return (
    <ProgressStepModal
      kind="horizontal"
      noPaddingX
      onClose={onClose}
      title={t("alerts.confirmFraud.modal.title")}
      subtitle={t("alerts.confirmFraud.modal.subtitle", { number: alert.alertNumber })}
      loading={loading || loadingComplainAlert}
      error={mapToModalError(error || complainAlertError)}
      confirmButtonText={t("general.actions.confirm")}
      initialFocus="[data-decision-input]:first-of-type"
      {...modalProps}
    >
      {modalContent}
    </ProgressStepModal>
  );
};
